import { ref, computed, watch } from 'vue'
import { usePersonalsStore } from '@/stores/personals'
import { useRouter, useRoute } from 'vue-router'

export function usePersonals() {
  const store = usePersonalsStore()
  const router = useRouter()
  const route = useRoute()

  // No search functionality - simple chronological listing

  // Computed
  const filteredAds = computed(() => {
    return store.personalAds
  })

  const hasAds = computed(() => {
    return filteredAds.value.length > 0
  })

  const isLoading = computed(() => {
    return store.loading
  })

  const error = computed(() => {
    return store.error
  })

  // Methods
  async function loadAds(loadMore = false) {
    await store.fetchPersonalAds(
      null, // no search
      loadMore
    )
  }

  async function loadMore() {
    if (!store.pagination.hasMore || isLoading.value) return
    await loadAds(true)
  }

  function navigateToAd(adId) {
    router.push({ name: 'personal-detail', params: { id: adId } })
  }

  function navigateToCreate() {
    router.push({ name: 'create-personal' })
  }

  function navigateToMyAds() {
    router.push({ name: 'my-personals' })
  }

  function navigateToMyReplies() {
    router.push({ name: 'my-replies' })
  }

  return {
    // Computed
    filteredAds,
    hasAds,
    isLoading,
    error,
    pagination: computed(() => store.pagination),
    
    // Methods
    loadAds,
    loadMore,
    navigateToAd,
    navigateToCreate,
    navigateToMyAds,
    navigateToMyReplies,
    clearError: store.clearError
  }
}

export function usePersonalAd(adId) {
  const store = usePersonalsStore()
  const router = useRouter()

  // Reply form state
  const replyForm = ref({
    message: '',
    isSubmitting: false
  })

  const replyErrors = ref({})

  // Computed
  const ad = computed(() => store.currentAd)
  const replies = computed(() => store.currentAdReplies)
  const isLoading = computed(() => store.loading)
  const error = computed(() => store.error)
  const canReply = computed(() => store.canReply)

  // Methods
  async function loadAd() {
    if (!adId) return
    await store.fetchPersonalAd(adId)
  }

  async function loadReplies() {
    if (!adId) return
    try {
      await store.fetchAdReplies(adId)
    } catch (err) {
      // Replies are only visible to poster, so this might fail
      // This is expected behavior for privacy protection
      console.log('Could not load replies (this is normal if you are not the poster)')
    }
  }

  function validateReply() {
    const errors = {}
    
    if (!replyForm.value.message.trim()) {
      errors.message = 'Message is required'
    } else if (replyForm.value.message.trim().length < 10) {
      errors.message = 'Message must be at least 10 characters'
    } else if (replyForm.value.message.trim().length > 500) {
      errors.message = 'Message must be less than 500 characters'
    }

    replyErrors.value = errors
    return Object.keys(errors).length === 0
  }

  async function submitReply() {
    if (!validateReply() || replyForm.value.isSubmitting) return

    replyForm.value.isSubmitting = true
    try {
      // Submit reply and capture inserted reply data
      const reply = await store.replyToAd(adId, replyForm.value.message)

      // Reset form
      replyForm.value.message = ''
      replyErrors.value = {}

      // Show success message and include reply ID for highlighting
      return { success: true, message: 'Reply sent successfully!', reply }
    } catch (err) {
      return { success: false, message: err.message }
    } finally {
      replyForm.value.isSubmitting = false
    }
  }

  function goBack() {
    router.go(-1)
  }

  // Cleanup on unmount
  function cleanup() {
    store.clearCurrentAd()
    replyForm.value = { message: '', isSubmitting: false }
    replyErrors.value = {}
  }

  return {
    // State
    replyForm,
    replyErrors,
    
    // Computed
    ad,
    replies,
    isLoading,
    error,
    canReply,
    
    // Methods
    loadAd,
    loadReplies,
    validateReply,
    submitReply,
    goBack,
    cleanup,
    clearError: store.clearError
  }
}

export function useMyPersonals() {
  const store = usePersonalsStore()
  const router = useRouter()

  // Computed
  const myAds = computed(() => store.myAds)
  const myReplies = computed(() => store.myReplies)
  const isLoading = computed(() => store.loading)
  const error = computed(() => store.error)
  const canCreateAd = computed(() => store.canCreateAd)
  const activeAdsCount = computed(() => store.activeAdsCount)

  // Methods
  async function loadMyAds() {
    await store.fetchMyAds()
  }

  async function loadMyReplies() {
    await store.fetchMyReplies()
  }

  async function deleteAd(adId) {
    if (!confirm('Are you sure you want to delete this ad? This action cannot be undone.')) {
      return
    }
    
    try {
      await store.deleteAd(adId)
      return { success: true, message: 'Ad deleted successfully' }
    } catch (err) {
      return { success: false, message: err.message }
    }
  }

  function viewAdReplies(adId) {
    router.push({ name: 'ad-replies', params: { id: adId } })
  }

  function navigateToCreate() {
    router.push({ name: 'create-personal' })
  }

  function navigateToAd(adId) {
    router.push({ name: 'personal-detail', params: { id: adId } })
  }

  function navigateToEdit(adId) {
    router.push({ name: 'edit-personal', params: { id: adId } })
  }

  return {
    // Computed
    myAds,
    myReplies,
    isLoading,
    error,
    canCreateAd,
    activeAdsCount,
    
    // Methods
    loadMyAds,
    loadMyReplies,
    deleteAd,
    viewAdReplies,
    navigateToCreate,
    navigateToAd,
    navigateToEdit,
    clearError: store.clearError
  }
}

export function useCreatePersonal() {
  const store = usePersonalsStore()
  const router = useRouter()

  // Form state
  const form = ref({
    title: '',
    body: '',
    isSubmitting: false
  })

  const errors = ref({})

  // Computed
  const canCreate = computed(() => store.canCreateAd)
  const activeAdsCount = computed(() => store.activeAdsCount)

  // Methods
  function validateForm() {
    const formErrors = {}
    
    if (!form.value.title.trim()) {
      formErrors.title = 'Title is required'
    } else if (form.value.title.trim().length < 5) {
      formErrors.title = 'Title must be at least 5 characters'
    } else if (form.value.title.trim().length > 100) {
      formErrors.title = 'Title must be less than 100 characters'
    }

    if (!form.value.body.trim()) {
      formErrors.body = 'Body is required'
    } else if (form.value.body.trim().length < 20) {
      formErrors.body = 'Body must be at least 20 characters'
    } else if (form.value.body.trim().length > 2000) {
      formErrors.body = 'Body must be less than 2000 characters'
    }

    errors.value = formErrors
    return Object.keys(formErrors).length === 0
  }

  async function submitForm() {
    if (!validateForm() || form.value.isSubmitting) return

    if (!canCreate.value) {
      return { success: false, message: 'Maximum of 3 active ads allowed' }
    }

    form.value.isSubmitting = true
    try {
      const ad = await store.createPersonalAd({
        title: form.value.title,
        body: form.value.body
      })

      // Reset form
      form.value = {
        title: '',
        body: '',
        isSubmitting: false
      }
      errors.value = {}

      // Navigate to the created ad
      router.push({ name: 'personal-detail', params: { id: ad.id } })
      
      return { success: true, message: 'Personal ad created successfully!' }
    } catch (err) {
      return { success: false, message: err.message }
    } finally {
      form.value.isSubmitting = false
    }
  }

  function goBack() {
    router.go(-1)
  }

  return {
    // State
    form,
    errors,
    
    // Computed
    canCreate,
    activeAdsCount,
    
    // Methods
    validateForm,
    submitForm,
    goBack
  }
}

export function useEditPersonal(adId) {
  const store = usePersonalsStore()
  const router = useRouter()

  // Form state
  const form = ref({
    title: '',
    body: '',
    isSubmitting: false
  })

  const errors = ref({})
  const isLoading = ref(false)
  const error = ref(null)

  // Methods
  async function loadAd() {
    if (!adId) return
    
    isLoading.value = true
    error.value = null
    
    try {
      const ad = await store.fetchPersonalAdForEdit(adId)
      if (ad) {
        form.value.title = ad.title
        form.value.body = ad.body
      }
    } catch (err) {
      error.value = err
    } finally {
      isLoading.value = false
    }
  }

  function validateForm() {
    const formErrors = {}
    
    if (!form.value.title.trim()) {
      formErrors.title = 'Title is required'
    } else if (form.value.title.trim().length < 5) {
      formErrors.title = 'Title must be at least 5 characters'
    } else if (form.value.title.trim().length > 100) {
      formErrors.title = 'Title must be less than 100 characters'
    }

    if (!form.value.body.trim()) {
      formErrors.body = 'Body is required'
    } else if (form.value.body.trim().length < 20) {
      formErrors.body = 'Body must be at least 20 characters'
    } else if (form.value.body.trim().length > 2000) {
      formErrors.body = 'Body must be less than 2000 characters'
    }

    errors.value = formErrors
    return Object.keys(formErrors).length === 0
  }

  async function submitForm() {
    if (!validateForm() || form.value.isSubmitting) return

    form.value.isSubmitting = true
    try {
      await store.updatePersonalAd(adId, {
        title: form.value.title,
        body: form.value.body
      })

      // Navigate back to the ad
      router.push({ name: 'personal-detail', params: { id: adId } })
      
      return { success: true, message: 'Personal ad updated successfully!' }
    } catch (err) {
      return { success: false, message: err.message }
    } finally {
      form.value.isSubmitting = false
    }
  }

  function goBack() {
    router.go(-1)
  }

  return {
    // State
    form,
    errors,
    isLoading,
    error,
    
    // Methods
    loadAd,
    validateForm,
    submitForm,
    goBack
  }
}
